import ApiCosts from '@components/ApiCosts';
import CommandPaletteProvider from '@components/Common/CommandPalette/CommandPaletteProvider';
import DesktopOnly from '@components/Common/DesktopOnly';
import ErrorBoundary from '@components/Common/ErrorBoundary';
import MobileNav from '@components/Common/MobileNav';
import ApplicationWorkspace from '@components/ApplicationWorkspace';
import Applications from '@components/Applications';
import NotificationsPage from '@components/Notifications';
import Companies from '@components/Companies';
import CompanyCreatePopup from '@components/Companies/CompanyCreatePopup';
import CompanyRequests from '@components/CompanyRequests';
import Analytics from '@components/Analytics';
import EmailCampaigns from '@components/EmailCampaigns';
import DripSequenceRecord from '@components/EmailOperations/DripSequenceRecord';
import EmailRecord from '@components/EmailOperations/EmailRecord';
import Employers from '@components/Employers';
import EmployerWorkspace from '@components/EmployerWorkspace';
import ExternalJobs from '@components/ExternalJobs';
import SourceWorkspace from '@components/ExternalJobs/SourceWorkspace';
import FreeJobAlerts from '@components/FreeJobAlerts';
import Guides from '@components/Guides';
import Seo from '@components/Seo';
import SeoPageWorkspace from '@components/Seo/SeoPageWorkspace';
import GuideEditor from '@components/Guides/GuideEditor';
import GuideImportPage from '@components/Guides/Import/ImportPage';
import GuideImportBatches from '@components/Guides/Import/BatchesList';
import GuideImportDashboard from '@components/Guides/Import/BatchDashboard';
import Agreements from '@components/Agreements';
import Ambassadors from '@components/Ambassadors';
import AmbassadorWorkspace from '@components/Ambassadors/AmbassadorWorkspace';
import HR from '@components/HR';
import Leads from '@components/Leads';
import LeadWorkspace from '@components/Leads/LeadWorkspace';
import Jobs from '@components/Jobs';
import JobWorkspace from '@components/JobWorkspace';
import CompanyWorkspace from '@components/CompanyWorkspace';
import JobCreatePopup from '@components/Jobs/JobCreatePopup';
import JobSeekers from '@components/JobSeekers';
import JobSeekerWorkspace from '@components/JobSeekerWorkspace';
import Login from '@components/Login';
import MyAccounts from '@components/MyAccounts';
import NotFound from '@components/NotFound';
import PushPrompt from '@components/PushPrompt';
import Settings from '@components/Settings';
import Referrals from '@components/Referrals';
import ReferralWorkspace from '@components/Referrals/ReferralWorkspace';
import Sidebar from '@components/Sidebar';
import Topbar from '@components/Topbar';
import Subscriptions from '@components/Subscriptions';
import PromoCodes from '@components/PromoCodes';
import PromoCodeCreate from '@components/PromoCodes/PromoCodeCreate';
import PromoCodeWorkspace from '@components/PromoCodes/PromoCodeWorkspace';
import UserDetails from '@components/UserDetails';
import UserRBAC from '@components/UserRBAC';
import {
  canAccessExternalJobs,
  canAccessGuides,
  canAssignJobs,
  canEditMarketing,
  canViewEmailCampaigns,
  homeRouteFor,
  isHrTeam,
  landingRouteFor,
  postSignInRoute,
  canWriteJobsAndCompanies,
  isKnownStaffRole,
  isSuperAdmin,
} from '@components/Common/Utils/roles';
import type { RootState } from '@store/rootReducer';
import { Alert, Box, Container, Snackbar } from '@mui/material';
import { clearLoaderStatus } from '@store/features/ui/actions';
import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import JobSeekersImportPage from '../JobSeekers/JobSeekersImport';
import './index.scss';

// C8 RBAC: Super-Admin oversight routes (HR assignment, user management) are off-limits to
// dedicated-HR users. Direct-URL access is redirected to the dashboard.
//
// Exported for the unit test. These two are the only thing standing between a dedicated HR and
// twenty Super-Admin routes, and they were untestable while private — nothing in the suite mounted
// a redux Provider, so no test could give a component a role at all.
export const NonHrRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequireRoles allow={isSuperAdmin}>{children}</RequireRoles>
);

/**
 * The one route guard.
 *
 * `NonHrRoute` was "not HR", which is the same fail-open shape the role helpers carried: a role
 * nobody had named yet passed straight through. `allow` is a predicate over the role, so a route
 * states who may see it rather than who may not.
 *
 * It waits for the profile before deciding. That matters now that an absent role is unprivileged:
 * `/api/me` is a request, so on a hard refresh the role is briefly undefined, and deciding then
 * would bounce a legitimate Super Admin off their own page. Rendering nothing for that moment is
 * the correct answer to "I do not know yet" — the redirect must mean "not allowed", never "not
 * loaded".
 */
export const RequireRoles: React.FC<{
  allow: (role?: string | null) => boolean;
  children: React.ReactNode;
}> = ({ allow, children }) => {
  const role = useSelector((state: RootState) => state.loginUserProfile.profile?.role);
  const status = useSelector((state: RootState) => state.loginUserProfile.status);

  if (status === 'loading' || status === 'idle') return null;
  return allow(role) ? <>{children}</> : <Navigate replace to={homeRouteFor(role)} />;
};

/** Guides and SEO: Super Admins and Marketing, and an HR Lead to read (canViewMarketing). */
export const GuidesRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequireRoles allow={canAccessGuides}>{children}</RequireRoles>
);

/**
 * The guide authoring screens: the editor, a new guide, bulk import. Only the roles that write
 * guides. An HR Lead reads guides from the list and its Preview; the editor autosaves drafts as
 * you type, so there is no read-only version of it to give her (owner, 2026-11-03).
 */
export const GuideAuthoringRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequireRoles allow={canEditMarketing}>{children}</RequireRoles>
);

/** Email campaigns: Super Admins, and an HR Lead to read. Every write is Super Admin on the BFF. */
export const EmailCampaignsRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequireRoles allow={canViewEmailCampaigns}>{children}</RequireRoles>
);

/** Job assignment (/hr): Super Admins and HR Leads. */
export const JobAssignmentRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequireRoles allow={canAssignJobs}>{children}</RequireRoles>
);

/**
 * `/email-campaigns/drips/:sequenceId` -> `/email-campaigns/drip-sequences/:sequenceId`.
 *
 * A component, not `<Navigate to="…">`: React Router cannot interpolate a param into a declarative
 * target, and dropping the id would land the user on a record page with nothing to show — the same
 * defect `/analytics/:tab` has, where the tab is silently discarded.
 */
const LegacyDripSequenceRedirect: React.FC = () => {
  const { sequenceId } = useParams<{ sequenceId: string }>();
  return <Navigate replace to={`/email-campaigns/drip-sequences/${sequenceId}`} />;
};

/**
 * `/analytics/:tab` -> `/dashboard/:tab`.
 *
 * Was `<Navigate to="/dashboard">`, which discarded the tab, so every bookmarked
 * `/analytics/funnel` quietly landed on Overview and looked like the dashboard had lost its state.
 */
const LegacyAnalyticsTabRedirect: React.FC = () => {
  const { tab } = useParams<{ tab: string }>();
  return <Navigate replace to={tab ? `/dashboard/${tab}` : '/dashboard'} />;
};

// C8 RBAC: HR-only surfaces (e.g. "My Accounts", the HR's assigned-client worklist) are
// meaningless to Super Admins. Direct-URL access by a non-HR is redirected to the dashboard.
export const HrOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequireRoles allow={isHrTeam}>{children}</RequireRoles>
);

interface LayoutProps {
  loginUserProfile: any;
}




const LayoutContent: React.FC<LayoutProps> = ({ loginUserProfile }) => {
  const isLoading = loginUserProfile?.status === 'loading';
  const isAuthenticated = loginUserProfile?.status === 'succeeded';
  const role: string | undefined = loginUserProfile?.profile?.role;
  const location = useLocation();
  const loaderStatus = useSelector((state: RootState) => state.ui.loaderStatus);
  const dispatch = useDispatch();

  // Names the browser tab for the current route. The console is an SPA, so without this every
  // screen kept index.html's static title and two console tabs were indistinguishable.
  useDocumentTitle();

  useEffect(() => { }, [loginUserProfile]);

  return (
    <Container disableGutters className="layout-shell" maxWidth={false}>
        <Routes>
          {/* `/` opens the role's own start page. Signed out, it goes to /login WITHOUT a `from`:
              recording `/` there would make it a destination, and every HR would land on the
              dashboard instead of their managed clients. */}
          <Route
            element={<Navigate replace to={isAuthenticated ? landingRouteFor(role) : '/login'} />}
            path="/"
          />
          <Route
            element={
              isAuthenticated ? (
                <Navigate to={postSignInRoute(role, location.state?.from)} />
              ) : (
                <Login isLoading={isLoading} loginUserProfile={loginUserProfile} />
              )
            }
            path="/login"
          />
          <Route
            element={
              isAuthenticated ? (
                <CommandPaletteProvider>
                <Box className="grid">
                  <Sidebar />
                  <Box className="main-grid">
                    <MobileNav />
                    <Topbar />
                    <Box className="main-paper">
                      {/* Above the page and outside its scroller, so it stays put while the
                          page scrolls and every staff role sees it on whichever page they land. */}
                      <PushPrompt />
                      {(loaderStatus.status !== '' || !isEmpty(loaderStatus.message)) && (
                        <Snackbar
                          open
                          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                          autoHideDuration={6000}
                          onClose={() => dispatch(clearLoaderStatus())}
                        >
                          <Alert
                            className="layout-alert"
                            severity={loaderStatus.status === 'success' ? 'success' : 'error'}
                            variant="filled"
                          >
                            {isEmpty(loaderStatus.message)
                              ? loaderStatus.status === 'success'
                                ? 'Save is successful'
                                : 'Save failed'
                              : loaderStatus.message}
                          </Alert>
                        </Snackbar>
                      )}
                      <div className="main-content-container">
                        {/* Inside the shell on purpose: a page that throws
                            should lose the page, not the sidebar and topbar.
                            Keyed by pathname so navigating away clears it. */}
                        <ErrorBoundary key={location.pathname} label="this page">
                        <Routes>
                          {/*
                            Two groups, because "Guides only" is a rule about who the user is and it has to be
                            applied ABOVE the page component.
                          
                            A sibling redirect loses a race it cannot win: the CRM list pages sync their filters
                            into the URL on mount, so the page navigated straight back over the redirect and a
                            Marketing user stayed on /jobs. A parent route renders its guard first, so the page
                            never mounts and has nothing to sync.
                          
                            Roughly twenty of the routes below carried no guard at all, which is why hiding nav
                            entries was never going to be enough.
                          */}
                          <Route element={<GuidesRoute><Outlet /></GuidesRoute>}>
                            <Route element={<GuidesRoute><Guides /></GuidesRoute>} path="/guides" />
                            {/* SEO, GEO, AEO tracker: the Guides audience (Super Admin + Marketing), so the same guard. */}
                            <Route element={<GuidesRoute><Seo /></GuidesRoute>} path="/seo" />
                            <Route element={<GuidesRoute><SeoPageWorkspace /></GuidesRoute>} path="/seo/pages/:pageId" />
                            <Route element={<GuidesRoute><SeoPageWorkspace /></GuidesRoute>} path="/seo/pages/:pageId/:tab" />
                            <Route element={<GuideAuthoringRoute><DesktopOnly minWidth={1024} title="New guide" reason="Importing a document and reviewing the extracted sections needs a desktop-width canvas."><GuideEditor /></DesktopOnly></GuideAuthoringRoute>} path="/guides/new" />
                            {/* Bulk import (Phase 2c). Static segments, so they rank above /guides/:guideId.
                                Desktop-gated like the editor: staging reports and the per-file dashboard need a
                                wide canvas, and the whole flow fires its data layer only once past the gate. */}
                            <Route element={<GuideAuthoringRoute><DesktopOnly minWidth={1024} title="Bulk import" reason="Uploading a batch and tracking per-file generation needs a desktop-width canvas."><GuideImportPage /></DesktopOnly></GuideAuthoringRoute>} path="/guides/import" />
                            <Route element={<GuideAuthoringRoute><GuideImportBatches /></GuideAuthoringRoute>} path="/guides/import/batches" />
                            <Route element={<GuideAuthoringRoute><DesktopOnly minWidth={1024} title="Import batch" reason="The per-file batch dashboard needs a desktop-width canvas."><GuideImportDashboard /></DesktopOnly></GuideAuthoringRoute>} path="/guides/import/batches/:batchId" />
                            <Route element={<GuideAuthoringRoute><GuideEditor /></GuideAuthoringRoute>} path="/guides/:guideId" />
                          </Route>
                          <Route element={<RequireRoles allow={isKnownStaffRole}><Outlet /></RequireRoles>}>
                            {/*
                              Every staff role (Super Admin, HR, Marketing) under one parent that
                              refuses an unknown role. Marketing was "Guides only" until 2026-10-19;
                              it now sees what an HR sees, and the routes that create records are
                              guarded on their own below (canWriteJobsAndCompanies).
                            */}
                            {/* Unified dashboard: an "Overview" tab (platform stats) plus the
                                analytics tabs, all on one page. Analytics is visible to both
                                roles; the resolver scopes an HR to their own clients. */}
                            <Route element={<Analytics />} path="/dashboard" />
                            <Route element={<Analytics />} path="/dashboard/:tab" />
                            {/* Legacy /analytics links land on the unified dashboard. */}
                            <Route element={<Navigate replace to="/dashboard" />} path="/analytics" />
                            <Route element={<LegacyAnalyticsTabRedirect />} path="/analytics/:tab" />
                            <Route element={<HrOnlyRoute><MyAccounts /></HrOnlyRoute>} path="/my-accounts" />
                            <Route element={<Jobs />} path="/jobs" />
                            <Route element={<RequireRoles allow={canWriteJobsAndCompanies}><JobCreatePopup /></RequireRoles>} path="/jobs/new" />
                            {/* The entity workspace is the only detail view; there is no /legacy. */}
                            {/* Each record workspace answers on both its bare path and one
                                carrying the open tab, so a tab can be linked to and survives a
                                reload. The bare path renders the default tab rather than
                                redirecting: a redirect entry in e2e/routes.mjs carries no shell or
                                wait, so redirecting would drop the shell assertion. */}
                            <Route element={<JobWorkspace />} path="/jobs/:jobId" />
                            <Route element={<JobWorkspace />} path="/jobs/:jobId/:tab" />
                            <Route element={<Companies />} path="/companies" />
                            <Route element={<RequireRoles allow={canWriteJobsAndCompanies}><CompanyCreatePopup /></RequireRoles>} path="/companies/new" />
                            {/* The entity workspace is the only detail view; there is no /legacy. */}
                            <Route element={<CompanyWorkspace />} path="/companies/:companyId" />
                            <Route element={<CompanyWorkspace />} path="/companies/:companyId/:tab" />
                            <Route element={<JobSeekers />} path="/jobseekers" />
                            {/* Phase 4 — entity workspace is the primary detail view. */}
                            <Route element={<JobSeekerWorkspace />} path="/jobseekers/:jobSeekerId" />
                            <Route element={<JobSeekerWorkspace />} path="/jobseekers/:jobSeekerId/:tab" />
                            <Route element={<DesktopOnly minWidth={1024} title="Import job seekers" reason="Mapping columns and reviewing per-row outcomes needs a wide screen to line up correctly."><JobSeekersImportPage /></DesktopOnly>} path="/jobseekers/import" />
                            <Route element={<Employers />} path="/employers" />
                            {/* Phase 4 — entity workspace is the primary detail view. */}
                            <Route element={<EmployerWorkspace />} path="/employers/:employerId" />
                            <Route element={<EmployerWorkspace />} path="/employers/:employerId/:tab" />
                            <Route element={<JobAssignmentRoute><HR /></JobAssignmentRoute>} path="/hr" />
                            <Route element={<NonHrRoute><Leads /></NonHrRoute>} path="/leads" />
                            <Route element={<NonHrRoute><LeadWorkspace /></NonHrRoute>} path="/leads/:leadId" />
                            {/* Applications = List (JobApplicants) + Board (PipelineBoard) under one tabbed route. */}
                            <Route element={<Navigate replace to="/applications/list" />} path="/applications" />
                            <Route element={<Applications />} path="/applications/:tab" />
                            {/* Legacy paths redirect into the merged view. */}
                            <Route element={<Navigate replace to="/applications/list" />} path="/job-applications" />
                            <Route element={<Navigate replace to="/applications/board" />} path="/pipeline" />
                            {/* Detail popup route is shared by both tabs — keep it intact for deep links. */}
                            {/* Phase 4 — the entity workspace is now the primary detail view. */}
                            <Route element={<ApplicationWorkspace />} path="/job-applications/:applicationId" />
                            <Route element={<ApplicationWorkspace />} path="/job-applications/:applicationId/:tab" />
                            {/* Deep-link target only — nothing in the UI navigates here; requests arrive by email.
                                The comment that used to sit here claimed this kept "AI-ops sub-views
                                (relevancy/embedding)". It carries neither: grep the component, it is
                                four read-only sections plus approve and reject. */}
                            {/* The list lives at its own path now, not /companies?tab=requests.
                                Wrapped in NonHrRoute for the same reason the tab was hidden from
                                HR: the operations behind it are Super-Admin only on the BFF, so
                                an HR reaching the list would get a table that 403s. */}
                            <Route element={<NonHrRoute><Companies /></NonHrRoute>} path="/company-requests" />
                            <Route element={<NonHrRoute><CompanyRequests /></NonHrRoute>} path="/company-requests/:requestId" />
                            <Route element={<UserDetails />} path="/user-details" />
                            <Route element={<Navigate to="/email-campaigns/drip-enrollments" replace />} path="/drip-campaigns"   />
                            <Route element={<Navigate to="/email-campaigns/automation-rules" replace />} path="/automated-emails" />
                            <Route element={<Navigate to="/dashboard/email"                  replace />} path="/email-analytics"  />
                            <Route element={<Navigate to="/email-campaigns/send-log"         replace />} path="/email-tracking"   />
                            <Route element={<Navigate to="/email-campaigns/templates"        replace />} path="/email-templates"  />
                            <Route element={<Navigate to="/email-campaigns/emails"           replace />} path="/email-campaigns"  />
                            {/* Record routes are SIBLINGS of the tab shell, not children: the shell
                                render-and-hides all six panels at once, so nesting a record inside it
                                would mount five lists alongside. Mirrors /companies + /companies/:id. */}
                            <Route element={<EmailCampaignsRoute><EmailRecord /></EmailCampaignsRoute>} path="/email-campaigns/emails/:templateKey" />
                            <Route element={<EmailCampaignsRoute><EmailRecord /></EmailCampaignsRoute>} path="/email-campaigns/emails/:templateKey/:tab" />
                            <Route element={<EmailCampaignsRoute><DripSequenceRecord /></EmailCampaignsRoute>} path="/email-campaigns/drip-sequences/:sequenceId" />
                            {/* Permanent alias for the old sequence-record path. Needs a component
                                rather than <Navigate to="...">, because a declarative target cannot
                                interpolate the :sequenceId it has to carry across. */}
                            <Route element={<EmailCampaignsRoute><LegacyDripSequenceRedirect /></EmailCampaignsRoute>} path="/email-campaigns/drips/:sequenceId" />
                            <Route element={<EmailCampaignsRoute><EmailCampaigns /></EmailCampaignsRoute>} path="/email-campaigns/:tab" />
                            <Route element={<NonHrRoute><UserRBAC /></NonHrRoute>} path="/user-rbac" />
                            <Route element={<NonHrRoute><UserRBAC /></NonHrRoute>} path="/user-rbac/new" />
                            <Route element={<NonHrRoute><UserRBAC /></NonHrRoute>} path="/user-rbac/:userId" />
                            <Route element={<NonHrRoute><UserRBAC /></NonHrRoute>} path="/user-rbac/:userId/:tab" />
                            <Route element={<NonHrRoute><Settings /></NonHrRoute>} path="/job-metadata" />
                            <Route element={<NonHrRoute><Agreements /></NonHrRoute>} path="/agreements" />
                            {/* The page was called Contracts and was mostly a template editor. Old
                                links, bookmarks and the entry in anyone's history still point here. */}
                            <Route element={<Navigate to="/agreements" replace />} path="/contracts" />
                            <Route element={<Navigate to="/agreements" replace />} path="/contracts/new" />
                            <Route element={<Navigate to="/agreements" replace />} path="/contracts/:contractId" />
                            <Route element={<NonHrRoute><Subscriptions /></NonHrRoute>} path="/subscriptions" />
                            {/* City-expansion promo codes: Super Admin only, like Subscriptions, because a code hands out free posts. */}
                            <Route element={<NonHrRoute><PromoCodes /></NonHrRoute>} path="/promo-codes" />
                            <Route element={<NonHrRoute><PromoCodeCreate /></NonHrRoute>} path="/promo-codes/new" />
                            <Route element={<NonHrRoute><PromoCodeWorkspace /></NonHrRoute>} path="/promo-codes/:promoId" />
                            <Route element={<NonHrRoute><PromoCodeWorkspace /></NonHrRoute>} path="/promo-codes/:promoId/:tab" />
                            <Route element={<NonHrRoute><FreeJobAlerts /></NonHrRoute>} path="/free-job-alerts" />
                            <Route element={<NonHrRoute><Navigate to="/cloud-costs/all" replace /></NonHrRoute>} path="/cloud-costs" />
                            <Route element={<NonHrRoute><ApiCosts /></NonHrRoute>} path="/cloud-costs/:tab" />
                            <Route element={<NotificationsPage />} path="/notifications" />
                            {/* HR and Super Admin: their referral link; Super Admin also pays out. */}
                            <Route element={<RequireRoles allow={(r) => isSuperAdmin(r) || isHrTeam(r)}><Referrals /></RequireRoles>} path="/referrals" />
                            {/* One referred job seeker. Super Admin only, as the table that opens it is (and the backend's getReferredJobSeekers). */}
                            <Route element={<NonHrRoute><ReferralWorkspace /></NonHrRoute>} path="/referrals/people/:jobSeekerId" />
                            {/* HR Ambassadors: external accounts, their candidates and rewards, and suspending one.
                                Super Admin only, as every operation behind them is on the BFF and the backend. */}
                            <Route element={<NonHrRoute><Ambassadors /></NonHrRoute>} path="/ambassadors" />
                            <Route element={<NonHrRoute><AmbassadorWorkspace /></NonHrRoute>} path="/ambassadors/:ambassadorId" />
                            <Route element={<NonHrRoute><AmbassadorWorkspace /></NonHrRoute>} path="/ambassadors/:ambassadorId/:tab" />
                            {/* Super Admins and HR. Status changes and takedown are Super Admin only, in the page and the BFF. */}
                            <Route element={<RequireRoles allow={canAccessExternalJobs}><ExternalJobs /></RequireRoles>} path="/external-jobs" />
                            <Route element={<RequireRoles allow={canAccessExternalJobs}><SourceWorkspace /></RequireRoles>} path="/external-jobs/:sourceId" />
                            <Route element={<RequireRoles allow={canAccessExternalJobs}><SourceWorkspace /></RequireRoles>} path="/external-jobs/:sourceId/:tab" />
                            <Route element={<NotificationsPage />} path="/notifications/:tab" />
                            <Route element={<NotFound />} path="*" />
                          </Route>
                        </Routes>
                        </ErrorBoundary>
                      </div>
                    </Box>
                  </Box>
                </Box>
                </CommandPaletteProvider>
              ) : (
                <Navigate state={{ from: location.pathname + location.search }} to="/login" />
              )
            }
            path="*"
          />
        </Routes>
      </Container>
  );
};


const LayoutPresentation: React.FC<LayoutProps> = (props) => (
  <Router>
    <LayoutContent {...props} />
  </Router>
);

export default LayoutPresentation;